export interface FootPosition { x: number; y: number; z: number }
interface FootState {
  x: number; y: number; z: number;
  advance: number; retreat: number; swinging: boolean;
}

/** Detect the swing-to-support transition of each foot in the FINISHED pose.
 * During swing the ankle travels along the movement direction; at heel strike
 * it reverses and travels backward relative to the body. Travel hysteresis
 * rejects toe pivots and tiny pose jitter without assuming clip length, stride
 * count, playback speed, or left/right ordering. Coordinates are avatar-local,
 * so world translation, planet rotation and origin shifts cannot create steps. */
export class FootfallTracker {
  private feet: FootState[] = [];
  reset(): void { this.feet.length = 0; }

  update(left: FootPosition, right: FootPosition, fwd: number, lateral: number): number {
    const length = Math.hypot(fwd, lateral);
    if (length < .1 || ![length, left.x, left.y, left.z, right.x, right.y, right.z].every(Number.isFinite)) {
      this.reset(); return 0;
    }
    fwd /= length; lateral /= length;
    let contacts = 0;
    for (let side = 0; side < 2; side++) {
      const point = side === 0 ? left : right;
      let foot = this.feet[side];
      if (!foot) {
        this.feet[side] = { ...point, advance: 0, retreat: 0, swinging: false };
        continue;
      }
      const dx = point.x - foot.x, dy = point.y - foot.y, dz = point.z - foot.z;
      foot.x = point.x; foot.y = point.y; foot.z = point.z;
      // A body/pose swap is not a foot strike.
      if (Math.hypot(dx, dy, dz) > .5) {
        foot.advance = foot.retreat = 0; foot.swinging = false;
        continue;
      }
      const travel = dx * lateral - dz * fwd;
      if (!foot.swinging) {
        foot.advance = Math.max(0, foot.advance + travel);
        if (foot.advance >= .07) { foot.swinging = true; foot.retreat = 0; }
      } else {
        foot.retreat = Math.max(0, foot.retreat - travel);
        // Sprint clips briefly reverse during heel recovery behind the body.
        // Plant only in the forward half of the stride (5 cm centre tolerance).
        // Do not require downward travel: crouch/grounding can lift a planted
        // ankle while it is already moving backward in the support phase.
        const ahead = point.x * lateral - point.z * fwd;
        if (foot.retreat >= .012 && ahead >= -.05) {
          contacts |= 1 << side;
          foot.advance = foot.retreat = 0;
          foot.swinging = false;
        }
      }
    }
    return contacts;
  }
}
